import React, { useState, useRef, useEffect, memo } from "react";
import PropTypes from "prop-types";
import {
  handleMouseEnter,
  handleMouseLeave,
  navigateToCell,
} from "./shared.js";
import { LOCATION_REQUIRED_FIELDS } from "../../constants";

export const RenderNoteEditable = memo(
  ({
    value: initialValue,
    row,
    rowCount,
    column,
    onCellInputBlur,
    viewOnly,
  }) => {
    const [value, setValue] = useState(initialValue || "");
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { index } = row;
    const { id } = column;
    const location = row.values?.pulloutLocation;
    const isRequired = LOCATION_REQUIRED_FIELDS[location]?.some(
      ({ field }) => field === id,
    );
    const showRequiredIndicator = isRequired && !value;
    const textareaRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    useEffect(() => {
      setValue(initialValue || "");
    }, [initialValue]);

    useEffect(() => {
      if (textareaRef.current) {
        if (isFocused || isHovered) {
          // Reset height to auto to recalculate based on content
          textareaRef.current.style.height = "auto";
          // Set height to match scrollHeight
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        } else {
          // On blur or mouse out, return to the original height
          textareaRef.current.style.height = "85%";
        }
      }
    }, [value, isFocused, isHovered]);

    return (
      <textarea
        ref={textareaRef}
        id={`${id}-${index}`}
        placeholder={isRequired ? "Comments Required" : ""}
        style={{
          width: "100%",
          minWidth: "150px",
          height: "85%",
          overflow: "hidden",
          resize: "none",
          transition: isFocused ? "none" : "height 0.3s ease",
          fontWeight: viewOnly ? "bold" : "normal",
          backgroundColor: viewOnly
            ? "#F2F2F2"
            : showRequiredIndicator
              ? "#fff3f3"
              : "",
          color: viewOnly ? "black" : "",
          cursor: viewOnly ? "not-allowed" : "text",
          boxSizing: "border-box",
          border: showRequiredIndicator ? "2px solid red" : "",
        }}
        maxLength="4000"
        value={value}
        onChange={(e) => !viewOnly && setValue(e.currentTarget.value)}
        onFocus={() => !viewOnly && setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          const newValue = e.currentTarget.value;
          if (newValue !== (initialValue || "")) {
            onCellInputBlur(newValue, index, id);
          }
        }}
        onKeyDown={(e) => {
          const { selectionStart, selectionEnd } = e.target;
          const atStart = selectionStart === 0 && selectionEnd === 0;
          const atEnd =
            selectionStart === value.length && selectionEnd === value.length;

          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            navigateToCell(id, index, e.key, rowCount);
          } else if (e.key === "ArrowLeft" && atStart) {
            e.preventDefault();
            navigateToCell(id, index, e.key, rowCount);
          } else if (e.key === "ArrowRight" && atEnd) {
            e.preventDefault();
            navigateToCell(id, index, e.key, rowCount);
          }
        }}
        onMouseEnter={() => handleMouseEnter(hoverTimeoutRef, setIsHovered)}
        onMouseLeave={() => handleMouseLeave(hoverTimeoutRef, setIsHovered)}
      />
    );
  },
);

RenderNoteEditable.displayName = "RenderNoteEditable";

RenderNoteEditable.propTypes = {
  value: PropTypes.string,
  row: PropTypes.shape({
    index: PropTypes.number.isRequired,
    values: PropTypes.shape({
      pulloutLocation: PropTypes.string,
    }),
    original: PropTypes.shape({
      signOutComments: PropTypes.string,
    }).isRequired,
  }).isRequired,
  rowCount: PropTypes.number.isRequired,
  column: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onCellInputBlur: PropTypes.func.isRequired,
  viewOnly: PropTypes.bool.isRequired,
};
